/**
 * 63.14 step 2b, the terraces' build (ARCHITECTURE §5.22 "Terracing under pre-generated bases"; DECISIONS 205 (1): the
 * owner, "make it seem like some intentional terracing and earthmoving has happened under bases that are pre-generated";
 * on the sheet: "prefer flatter land but terrace when necessary"): the ground rule a terrace imposes, read by the fine
 * ground (core/fineTerrain.ts personSurfaceRadius) and the drawn patch alike, and the coarse **apron** it derives for the
 * far mesh. A terrace is one record (`Terrace`, types/ground.ts; `PlanetState.terraces`): a pad — its centre, its frame,
 * its half extents — at a `level` (an absolute radius, fixed when it is laid), with a batter band round it. **Inside the
 * pad the ground is the level, flat to the bit**; outside, within the band, the natural ground is held to the batter from
 * the pad's edge — a **cut** face where the natural ground stands above the level (TERRACE.cutBatter rise over run, the
 * revetment look of kit 2's plinth), a **fill** bank where it lies below (TERRACE.fillBatter, a berm's slope); past the band
 * the natural ground is untouched. The nearest pad rules where bands overlap (71.5 step 2, `PlanetState.shapingOrder`: the
 * log's order, each acting on what the earlier made, and a crater or mound under a pad dents it — padDent). All in world units, so the record is
 * self-contained in the terrain log (types/inputs.ts TerrainEdit `kind: 'terrace'`: a replay or a sector re-fight lays it
 * again from the record, R13). Pure; no trig beyond the cell index's cosine.
 *
 * **Ramps** (69.42; `Terrace.ramps`, core/terraces.ts gateRamps): where a building on the pad has a vehicle gate onto a cut
 * face, straight out from it across the gate's width and a margin, the ramp: graded from the pad's level at TERRACE.rampBatter
 * out to where it meets the ground (its `length`, found when it is laid) — a truck climbs it. 71.7 (2): steeper (its `grade`,
 * to TERRACE.rampSteepest) where a hill outruns that grade over the lane's reach; refitted once every terrace is laid.
 *
 * Not in this step (noted for the next): a foot ramp between pads, the
 * split of a site whose rise exceeds the step cap into two pads along the contour (the sites pass's), and a crater's bowl
 * masking the terrace inside it (§5.22 (13)): a shell on a pad moves the mesh under it today, and the pad's level stands.
 */
import type { PlanetState, Vec3 } from '../types';
import type { Shaping, Terrace } from '../types/ground';
import { dcos } from './dmath';
import { verticesNear } from './vertexCells';
import { getSurfaceRadius, lastSurfaceTriangle, getTerrainIndex, surfaceRadiusWith } from './surface';
import { bumpIndex } from './bumpIndex';
import { shapingRule } from './shaping';

/** The terrace's numbers (people where named so; the batters rise over run). */
export const TERRACE = {
  /** A cut face's batter: 1.5 of rise over 1 of run — near vertical, a body below cannot climb it (the walk's slope limit is 1.2). */
  cutBatter: 1.5,
  /** A fill bank's: a berm's slope, walkable. */
  fillBatter: 0.5,
  /** 69.42: a vehicle ramp's grade across a cut face at a gate (a truck climbs 1.08; the cut's 1.5 stopped it at the motor pool's back gate). */
  rampBatter: 0.6,
  /** 69.42: a ramp's half width past its gate's half opening (people): the hull's width and a margin to steer in. */
  rampMarginPeople: 1.5,
  /** 69.42: how far a ramp's lane may reach out from the pad, in bands: past the band, till it meets the ground (at a 3.4-people cut the ramp stood 3 people up at the band's edge, and the ground stepped there). 71.7 (2): 3 → 4 — s71-1's pool's front stood 6 people under a hill rising 0.53 a person, which a ramp at rampSteepest meets 16.5 out. */
  rampReachBands: 4,
  /** 71.7 (2): the steepest a ramp is graded where the hill outruns rampBatter over its reach (the lane's end a 5-people cut face on s71-1's pool): under a truck's 1.08, with room for the drive's sampling. */
  rampSteepest: 0.9,
  /** How far out from the pad's edge the batter band reaches (people): the tallest step at the fill's batter. */
  bandPeople: 5,
  /** The tallest step a terrace makes (people); a footprint whose natural rise exceeds it is not terraced here (its site splits — the sites pass, later). */
  maxStepPeople: 2.5,
  /** The least rise under a footprint worth a terrace (people): over the relief's own hummocks (up to 1.2 people, which the plinth's bedding takes), so a real slope — flatter ground keeps the plinth (205: "prefer flatter land but terrace when necessary"). */
  minRisePeople: 1.5,
  /** Samples a side over a footprint when its site is read. */
  samples: 5,
  /** The coarse apron's slope (rise over run) the far mesh's corners under a pad are held to, either side of its level: the far mesh across the pad within a person of it. */
  apronSlope: 0.05,
} as const;

/** A terrace's reach from its centre (world): the pad's half diagonal and its band (a ramp's lane, its fill bank, past it). */
export function terraceReach(t: Pick<Terrace, 'halfX' | 'halfZ' | 'band' | 'ramps' | 'fillBanks'>): number {
  const b = t.fillBanks, band = t.ramps?.length ? t.band * TERRACE.rampReachBands : t.band;   // (69.42: a ramp's lane past the band)
  return Math.sqrt(t.halfX * t.halfX + t.halfZ * t.halfZ) + (b ? Math.max(band, b[0].reach, b[1].reach, b[2].reach, b[3].reach) : band);
}

/** The pad's sides a point (x, z) of its frame stands off, `dx`, `dz` outside its edges: Terrace.fillBanks' x+ / x− (or −1), z+ / z− (or −1). */
const sideX = (x: number, dx: number) => (dx > 0 ? (x > 0 ? 0 : 1) : -1), sideZ = (z: number, dz: number) => (dz > 0 ? (z > 0 ? 2 : 3) : -1);

/** How far out from its pad a terrace's rule reaches at (x, z) of its frame, `dx`, `dz` outside its edges (world): its band, or its fill bank's reach on that side (Terrace.fillBanks; off a corner, the farther of its two sides'). */
function bandAt(t: Terrace, x: number, z: number, dx: number, dz: number): number {
  const b = t.fillBanks;
  if (!b) return t.band;
  const i = sideX(x, dx), k = sideZ(z, dz);
  return Math.max(t.band, i >= 0 ? b[i].reach : 0, k >= 0 ? b[k].reach : 0);
}

/** A side's fill bank's line `d` outside the pad (Terrace.fillBanks): the band's (TERRACE.fillBatter) within it, the side's grade on from its edge, to its reach — or −∞ past it. */
function bankLine(t: Terrace, b: { reach: number; grade: number }, d: number): number {
  if (d >= b.reach) return -Infinity;
  return d < t.band ? t.level - TERRACE.fillBatter * d : t.level - TERRACE.fillBatter * t.band - b.grade * (d - t.band);
}

/** The fill banks' ground at (x, z), `d` outside the pad, over `natural` under the level: the higher of its sides' banks there (Terrace.fillBanks), the natural past them. */
function bankGround(t: Terrace, x: number, z: number, dx: number, dz: number, d: number, natural: number): number {
  const b = t.fillBanks!, i = sideX(x, dx), k = sideZ(z, dz);
  return Math.max(natural, i >= 0 ? bankLine(t, b[i], d) : -Infinity, k >= 0 ? bankLine(t, b[k], d) : -Infinity);
}

const reachMemo = new WeakMap<readonly Terrace[], { reach: number; radius: number; cosReach: number }>();
/** The list's widest reach and its cosine at the planet's radius, once per list (a lookup pays no trig). */
function widestReach(list: readonly Terrace[], radius: number): { reach: number; cosReach: number } {
  let r = reachMemo.get(list);
  if (r === undefined || r.radius !== radius) { let reach = 0; for (const t of list) reach = Math.max(reach, terraceReach(t)); r = { reach, radius, cosReach: dcos(reach / radius) }; reachMemo.set(list, r); }
  return r;
}

/**
 * The ground a terrace rule makes of `natural` at a point (x, z) of the terrace's frame (world units from its centre):
 * the level inside the pad; held to the batter within the band (its fill banks on past it: `fillBanks`); the natural
 * beyond. `d` is the point's distance outside the pad's edge (0 inside).
 */
export function terraceRule(t: Terrace, x: number, z: number, natural: number): number {
  const dx = Math.max(Math.abs(x) - t.halfX, 0), dz = Math.max(Math.abs(z) - t.halfZ, 0);
  if (dx === 0 && dz === 0) return t.level;
  const d = Math.sqrt(dx * dx + dz * dz);
  // (69.42: across a gate's ramp, out to its length, the ramp itself — graded from the pad's level at TERRACE.rampBatter, cut
  // and filled (a hummock's dip in it filled: clipped to the ground, one rose 1.26 out of it); where a hill steeper than the
  // ramp never met it, a cut face up to the ground at the lane's end — not a cliff: story-2's large site stepped 4.6 people)
  const r = t.ramps ? rampAt(t, x, z) : null;
  if (r && r.grade !== undefined && r.grade < 0) {
    // (the owner's stuck troops, Nov 2: a ramp down onto fill — the gate's lane a bank at the ramp's own grade, out to where it
    // met the ground: it fills, a hummock in it stands, ground over the level is the band's; past its end the side's bank and
    // the band carry on — under the ramp's line there — except where they would stand over its end (a ramp steeper than its
    // side's bank): the ground's there)
    if (natural <= t.level) {
      if (d < r.length) return Math.max(natural, t.level + r.grade * d);
      const end = t.level + r.grade * r.length, bank = t.fillBanks?.[r.side === 'x+' ? 0 : r.side === 'x-' ? 1 : r.side === 'z+' ? 2 : 3];
      if ((bank ? bankLine(t, bank, r.length) : r.length < t.band ? t.level - TERRACE.fillBatter * r.length : -Infinity) > end) return natural;
    }
  } else if (r && d < r.length) return Math.max(t.level + (r.grade ?? TERRACE.rampBatter) * d, natural - TERRACE.cutBatter * (r.length - d));
  if (t.fillBanks && natural <= t.level) return bankGround(t, x, z, dx, dz, d, natural);   // (the banks, on past the band)
  return shapingRule('set', TERRACE.cutBatter, TERRACE.fillBatter, t.band, d, t.level, natural);   // (71.5: the one shaping rule — a pad in `set`)
}

/**
 * 71.5 (ARCHITECTURE §5.36 (6)): a terrace as the one shaping record — a pad in `set` at its level, the terrace's batters,
 * its band the reach. Its ramps (69.42) are not in the record: they keep their own rule (graded up from the level, then a
 * cut back to the ground at the lane's end — not the clamp), read by terraceRule as before.
 */
export function terracePad(t: Terrace): Shaping {
  return {
    id: t.id, mode: 'set', cut: TERRACE.cutBatter, fill: TERRACE.fillBatter, reach: t.band,
    footprint: { kind: 'pad', dir: t.dir, facing: t.facing, halfX: t.halfX, halfZ: t.halfZ, level: t.level },
  };
}

/** 69.42: the ramp of `t` whose lane holds (x, z) of the terrace's frame — straight out from its edge of the pad, within its half width — or null. */
function rampAt(t: Terrace, x: number, z: number): NonNullable<Terrace['ramps']>[number] | null {
  for (const r of t.ramps!) {
    const out = r.side === 'x+' ? x - t.halfX : r.side === 'x-' ? -x - t.halfX : r.side === 'z+' ? z - t.halfZ : -z - t.halfZ;
    const along = r.side === 'x+' || r.side === 'x-' ? z : x;
    if (out > 0 && Math.abs(along - r.at) <= r.half) return r;
  }
  return null;
}

/** A unit direction's point in a terrace's frame (world units from the pad's centre; small angles: the chord for the arc). */
export function terraceLocal(t: Pick<Terrace, 'dir' | 'facing'>, radius: number, ux: number, uy: number, uz: number): { x: number; z: number } {
  const d = t.dir, f = t.facing;
  const ox = ux - d.x, oy = uy - d.y, oz = uz - d.z;
  const rx = f.y * d.z - f.z * d.y, ry = f.z * d.x - f.x * d.z, rz = f.x * d.y - f.y * d.x;   // across the pad (x), as a dig's frame (core/shaping.ts padLocal, the same sums)
  return { x: (ox * rx + oy * ry + oz * rz) * radius, z: (ox * f.x + oy * f.y + oz * f.z) * radius };
}

/**
 * The fine ground's read: the terraced ground at the unit direction (ux, uy, uz) given the natural ground there
 * (`natural`: the mesh, the relief and the digs — a radius; `coarse`: the mesh's alone), through the planet's bump index
 * (the terraces of this point's cell). The natural ground where no terrace reaches; the nearest terrace's rule where one or
 * more do — or, with `shapingOrder` (71.5 step 2), every one that reaches it in the list's (the log's) order, each dented.
 * `bowl` (the follow-up, DECISIONS 379): the craterlets' change here since the last terrace was laid (fineTerrain `padBowl`;
 * `natural` holds it) — with `shapingOrder`, every pad here is dented by it too.
 */
export function terraceGround(planet: Pick<PlanetState, 'terraces' | 'radius' | 'shapingOrder' | 'padLaid' | 'heightmap' | 'basePositions'>, ux: number, uy: number, uz: number, natural: number, coarse: number, bowl = 0): number {
  const terraces = planet.terraces!, radius = planet.radius;
  const { reach, cosReach } = widestReach(terraces, radius);
  const near = bumpIndex(terraces, radius, reach).at(ux, uy, uz);
  if (near.length === 0) return natural;
  if (planet.shapingOrder) return orderedGround(planet, near, cosReach, ux, uy, uz, natural, coarse, bowl);
  let best: Terrace | null = null, bestD = Infinity, bx = 0, bz = 0;
  for (let q = 0; q < near.length; q++) {
    const t = terraces[near[q]];
    if (ux * t.dir.x + uy * t.dir.y + uz * t.dir.z < cosReach) continue;
    const { x, z } = terraceLocal(t, radius, ux, uy, uz);
    const dx = Math.max(Math.abs(x) - t.halfX, 0), dz = Math.max(Math.abs(z) - t.halfZ, 0), d = Math.sqrt(dx * dx + dz * dz);
    if (d >= bandAt(t, x, z, dx, dz)) { const r = t.ramps ? rampAt(t, x, z) : null; if (!r || d >= r.length) continue; }   // (69.42: a ramp's lane reaches past the band)
    if (d < bestD) { best = t; bestD = d; bx = x; bz = z; }
  }
  return best ? terraceRule(best, bx, bz, natural) : natural;
}

/**
 * 71.5 step 2 (ARCHITECTURE §5.36 (6) *Order*, *Dents*): every terrace reaching the point, in the list's order (the bump
 * index keeps it: the log's), each acting on the ground the earlier left; a dented pad's rule reads the mesh as it was laid
 * (`g − δ`) and adds the mesh's change since (`+ δ`), so a crater dents a pad by its own bowl and a mound heaps on it; the
 * craterlets' change since (`bowl`) adds to every pad's δ (the follow-up, 379). One pad and no dent: terraceRule, bit for bit.
 */
function orderedGround(planet: Pick<PlanetState, 'terraces' | 'radius' | 'padLaid' | 'heightmap' | 'basePositions'>, near: readonly number[], cosReach: number, ux: number, uy: number, uz: number, natural: number, coarse: number, bowl: number): number {
  const terraces = planet.terraces!, radius = planet.radius, dents = planet.padLaid ? dentsOf(planet) : null;
  let g = natural, delta = NaN;
  for (let q = 0; q < near.length; q++) {
    const t = terraces[near[q]];
    if (ux * t.dir.x + uy * t.dir.y + uz * t.dir.z < cosReach) continue;
    const { x, z } = terraceLocal(t, radius, ux, uy, uz);
    const dx = Math.max(Math.abs(x) - t.halfX, 0), dz = Math.max(Math.abs(z) - t.halfZ, 0), d = Math.sqrt(dx * dx + dz * dz);
    if (d >= bandAt(t, x, z, dx, dz)) { const r = t.ramps ? rampAt(t, x, z) : null; if (!r || d >= r.length) continue; }
    let dd = bowl;
    if (dents && dents.pads.has(t.id)) {
      if (delta !== delta) delta = coarse - surfaceRadiusWith(getTerrainIndex(planet.basePositions), dents.laid!, planet.basePositions, radius, ux, uy, uz);
      dd = delta + bowl;
    }
    g = dd === 0 ? terraceRule(t, x, z, g) : terraceRule(t, x, z, g - dd) + dd;
  }
  return g;
}

/** 71.5 step 2: the pads a crater or mound has moved the mesh under since the last terrace was laid, the mesh as it was then, and how far any such pad's ground can rise over its level (a ceiling's bound). */
interface Dents { heightmap: Float32Array; laid: Float32Array | null; pads: Set<number>; rise: number }
const dentMemo = new WeakMap<NonNullable<PlanetState['padLaid']>, Dents>();
/** The planet's dents (a pure cache: by the kept heights' and the heightmap's identity). */
export function dentsOf(planet: Pick<PlanetState, 'padLaid' | 'heightmap' | 'basePositions' | 'radius'>): Dents {
  const kept = planet.padLaid!, hm = planet.heightmap;
  const m = dentMemo.get(kept);
  if (m && m.heightmap === hm) return m;
  const pads = new Set<number>();
  for (const p of kept) for (let k = 0; k < p.idx.length; k++) if (hm[p.idx[k]] !== p.h[k]) { pads.add(p.id); break; }
  let laid: Float32Array | null = null, rise = 0;
  if (pads.size) {
    laid = new Float32Array(hm);
    for (const p of kept) for (let k = 0; k < p.idx.length; k++) laid[p.idx[k]] = p.h[k];   // (every pad's taken at the same laying: they agree where they share)
    const inv = getTerrainIndex(planet.basePositions).invLen, bp = planet.basePositions, R = planet.radius, chord = longestChord(bp);
    for (const p of kept) {
      if (!pads.has(p.id)) continue;
      let top = -Infinity, low = Infinity;
      for (const i of p.idx) {
        const len = Math.sqrt(bp[i * 3] * bp[i * 3] + bp[i * 3 + 1] * bp[i * 3 + 1] + bp[i * 3 + 2] * bp[i * 3 + 2]);
        top = Math.max(top, len * (1 + hm[i] * inv[i]), R + hm[i]);
        low = Math.min(low, len * (1 + laid[i] * inv[i]), R + laid[i]);
      }
      rise = Math.max(rise, top - low + chord * chord * R);   // (a facet's sag between its corners, taken generously)
    }
  }
  const out: Dents = { heightmap: hm, laid, pads, rise };
  dentMemo.set(kept, out);
  return out;
}

/** The longest chord between a mesh triangle's corners, as unit directions (once per base positions): a point's facet's corners lie within it × the radius. */
const chordMemo = new WeakMap<Float32Array, number>();
function longestChord(bp: Float32Array): number {
  let c = chordMemo.get(bp);
  if (c !== undefined) return c;
  c = 0;
  const u = new Float64Array(9);
  for (let t = 0; t + 9 <= bp.length; t += 9) {
    for (let k = 0; k < 3; k++) { const x = bp[t + 3 * k], y = bp[t + 3 * k + 1], z = bp[t + 3 * k + 2], l = Math.sqrt(x * x + y * y + z * z) || 1; u[3 * k] = x / l; u[3 * k + 1] = y / l; u[3 * k + 2] = z / l; }
    for (const [a, b] of [[0, 1], [1, 2], [0, 2]]) { const dx = u[3 * a] - u[3 * b], dy = u[3 * a + 1] - u[3 * b + 1], dz = u[3 * a + 2] - u[3 * b + 2]; c = Math.max(c, Math.sqrt(dx * dx + dy * dy + dz * dz)); }
  }
  chordMemo.set(bp, c);
  return c;
}

/** 71.5 step 2: the heightmap's entries under a terrace's reach and a facet (every corner of a triangle a point in its reach can stand on). */
function keptVertices(planet: PlanetState, t: Terrace): Int32Array {
  const R = planet.radius, near = verticesNear(planet.basePositions, t.dir, (terraceReach(t) + longestChord(planet.basePositions) * R * 1.01) / R);
  return near ? Int32Array.from(near) : Int32Array.from({ length: planet.heightmap.length }, (_, i) => i);
}

/** 71.5 step 2: every terrace's mesh as it stands now — taken again as each is laid, so a later pad's apron is not a dent. */
export function padSnapshots(planet: PlanetState): NonNullable<PlanetState['padLaid']> {
  return (planet.terraces ?? []).map(t => { const idx = keptVertices(planet, t), h = new Float32Array(idx.length); for (let k = 0; k < idx.length; k++) h[k] = planet.heightmap[idx[k]]; return { id: t.id, idx, h }; });
}

/**
 * The mesh's facet under a pad (world): how far the corners of the triangles under its centre and its four corners
 * reach from the pad (the mesh is non-indexed: triangle `t`'s corners are entries 3t … 3t + 2). The apron's reach.
 */
export function padFacet(planet: PlanetState, t: Terrace): number {
  const R = planet.radius, d = t.dir, f = t.facing;
  const rx = f.y * d.z - f.z * d.y, ry = f.z * d.x - f.x * d.z, rz = f.x * d.y - f.y * d.x;
  let facet = 0;
  for (const [sx, sz] of [[0, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
    const x = sx * t.halfX, z = sz * t.halfZ;
    getSurfaceRadius(planet, { x: d.x + (rx * x + f.x * z) / R, y: d.y + (ry * x + f.y * z) / R, z: d.z + (rz * x + f.z * z) / R });
    const tri = lastSurfaceTriangle();
    if (tri < 0) continue;
    for (let k = 0; k < 3; k++) {
      const i = tri * 3 + k, bx = planet.basePositions[i * 3], by = planet.basePositions[i * 3 + 1], bz = planet.basePositions[i * 3 + 2];
      const len = Math.sqrt(bx * bx + by * by + bz * bz);
      const l = terraceLocal(t, R, bx / len, by / len, bz / len);
      const dx = Math.max(Math.abs(l.x) - t.halfX, 0), dz = Math.max(Math.abs(l.z) - t.halfZ, 0);
      facet = Math.max(facet, Math.sqrt(dx * dx + dz * dz));
    }
  }
  return facet;
}

/**
 * The coarse apron: the mesh's vertices inside the pad snapped to its level, and the corners of the triangles under it
 * (padFacet: every vertex as far out as they reach) held within TERRACE.apronSlope of it — a gentle worked slope the far
 * mesh can carry (a facet is tens of people at the human scale: the mesh cannot hold the fine rule's step, and a pad smaller
 * than a facet holds no vertex, so it is the corners' heights that level the mesh across it). A clamp, so it is idempotent
 * (laid twice, the same heights) and a replay lays it again from the record (§5.22: a derived edit, never stored). Coarse
 * only: the fine rule's band reads the clamped mesh as its natural ground. The planet's mass is unchanged (cut and fill, not
 * a mound). A new heightmap only when a vertex moves.
 */
export function terraceApron(planet: PlanetState, t: Terrace): PlanetState {
  if (planet.indestructible) return planet;
  const facet = padFacet(planet, t) + 1e-6, R = planet.radius;
  const reach = Math.sqrt(t.halfX * t.halfX + t.halfZ * t.halfZ) + facet;
  const near = verticesNear(planet.basePositions, t.dir, reach / R);
  const count = near ? near.length : planet.heightmap.length;
  let out: Float32Array | null = null;
  for (let k = 0; k < count; k++) {
    const i = near ? near[k] : k;
    const bx = planet.basePositions[i * 3], by = planet.basePositions[i * 3 + 1], bz = planet.basePositions[i * 3 + 2];
    const len = Math.sqrt(bx * bx + by * by + bz * bz);
    const ux = bx / len, uy = by / len, uz = bz / len;
    const { x, z } = terraceLocal(t, R, ux, uy, uz);
    const dx = Math.max(Math.abs(x) - t.halfX, 0), dz = Math.max(Math.abs(z) - t.halfZ, 0), d = Math.sqrt(dx * dx + dz * dz);
    if (d > facet) continue;
    // (69.22: a vertex whose triangles would reach ground kept — within its reach and a facet — stays where it is)
    if (d > 0 && t.apronKeep?.some(k => ux * k.dir.x + uy * k.dir.y + uz * k.dir.z > dcos(Math.min(Math.PI, (k.reach + facet) / R)))) continue;   // (the pad's own vertices always level)
    const natural = len + planet.heightmap[i], allow = TERRACE.apronSlope * d;
    const h = Math.fround(Math.max(t.level - allow, Math.min(t.level + allow, natural)) - len);
    if (h === planet.heightmap[i]) continue;
    (out ??= new Float32Array(planet.heightmap))[i] = h;
  }
  return out ? { ...planet, heightmap: out, initialHeightmap: planet.initialHeightmap ?? planet.heightmap } : planet;
}

/** `planet` with terrace `t` laid: its record (replacing an earlier one of the same id) and its apron. */
export function withTerrace(planet: PlanetState, t: Terrace): PlanetState {
  const terraces = [...(planet.terraces ?? []).filter(x => x.id !== t.id), t];
  const laid = terraceApron({ ...planet, terraces }, t);
  // (71.5 step 2: the mesh each pad's dents are read against; its follow-up, 379: the craterlets as they stood)
  return laid.shapingOrder ? { ...laid, padLaid: padSnapshots(laid), ...(laid.craterlets ? { padCraterlets: laid.craterlets } : {}) } : laid;
}

/** The terrace's centre on the ground (world). */
export function terraceCentre(planet: Pick<PlanetState, 'position' | 'radius'>, t: Pick<Terrace, 'dir'>): Vec3 {
  return { x: planet.position.x + t.dir.x * planet.radius, y: planet.position.y + t.dir.y * planet.radius, z: planet.position.z + t.dir.z * planet.radius };
}
